import dayjs from "dayjs";
import { compareDates } from "@/shared/utils";
import { DATE_FORMAT } from "@/shared/constants";
import { DateFilter } from "@/common/DateFilter";
import { DEFAULT_NOTE_TYPES, NOTE_STATUS } from "@/services/api";
import type { DateFilterProps } from "@/common/DateFilter";
import type { NotesTableRowData } from "../types/notes-table-types";
import type { NotesTableColumn, NotesTableColumnGetter, NotesTableColumnsProps } from "../types";
import { NOTES_TABLE_COLUMNS_KEYS } from "../notes-table-columns-keys";
import { DateCell } from "../../ui/Components/DateCell";
import { getRowTextColor } from "../../lib/get-row-text-color";

export type NotesTableNoteDateColumnProps = NotesTableColumnsProps<{
  onChangeFilterDateFrom?: DateFilterProps["onChangeFrom"];
  onChangeFilterDateTo?: DateFilterProps["onChangeTo"];
  defaultValueFilterFrom?: DateFilterProps["defaultValueFrom"];
  defaultValueFilterTo?: DateFilterProps["defaultValueTo"];
  colorize?: boolean;
}>;

export type NotesTableNoteDateColumnGetter = NotesTableColumnGetter<NotesTableNoteDateColumnProps>;

export const getNotesTableNoteDateColumn: NotesTableNoteDateColumnGetter = ({
  title = "Date",
  dataIndex = ["noteDate"],
  width = 125,
  minWidth = 125,
  key = NOTES_TABLE_COLUMNS_KEYS.noteDate,
  sorter = (a: NotesTableRowData, b: NotesTableRowData) =>
    !isNaN(compareDates({ firstDate: a?.noteDate, secondDate: b?.noteDate }))
      ? compareDates({ firstDate: a?.noteDate, secondDate: b?.noteDate }) >= 0
        ? -1
        : 1
      : isNaN(dayjs(a?.noteDate, DATE_FORMAT).day())
        ? -1
        : 1,
  showSorterTooltip = false,
  filterDropdown,
  onChangeFilterDateFrom = () => "",
  onChangeFilterDateTo = () => "",
  defaultValueFilterFrom,
  defaultValueFilterTo,
  render,
  colorize = false,
  ...props
}) => {
  const defaultDateFilter = () => (
    <DateFilter
      defaultValueFrom={defaultValueFilterFrom}
      defaultValueTo={defaultValueFilterTo}
      onChangeFrom={onChangeFilterDateFrom}
      onChangeTo={onChangeFilterDateTo}
    />
  );

  const defaultRender: NotesTableColumn["render"] = (_, record) => {
    return (
      <DateCell
        textColor={getRowTextColor(
          record,
          colorize && DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT === record.noteType.name,
        )}
        disabled={
          record.status === NOTE_STATUS.DONE &&
          record.noteType.name === DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT
        }
        date={record.noteDate}
      />
    );
  };

  return {
    title: title,
    dataIndex: dataIndex,
    width: width,
    minWidth: minWidth,
    key: key,
    sorter: sorter,
    showSorterTooltip: showSorterTooltip,
    filterDropdown: filterDropdown ? filterDropdown : defaultDateFilter,
    render: render ? render : defaultRender,
    ...props,
  };
};
